import {
  Button,
  Checkbox,
  DatePicker,
  dayjs,
  Divider,
  Icons,
  LinkButton,
  SearchField,
  Tag,
  TextField,
  Typography,
} from "@flash-tecnologia/hros-web-ui-v2";
import { useSelectedCompany } from "@flash-tecnologia/hros-web-utility";
import { documentNumberFormatter } from "@utils/document-number-formatter";
import { phoneFormatter } from "@utils/phone-formatter";
import { useEffect, useState } from "react";
import { Box } from "src/components/core-components/atoms/Box/box";
import { EllipsisTypography } from "src/components/core-components/atoms/EllipsisTypography/ellipsis-typography";
import { Tip } from "src/components/core-components/molecules/Tip/tip";
import {
  Dropdown,
  DropdownModes,
  Sizes as DropdownSizes,
} from "src/components/core-components/organisms/Dropdown/dropdown";
import { useTheme } from "styled-components";

import { MaskedScrollArea } from "@/components/core-components/organisms/MaskedScrollArea/masked-scroll-area";
import { useUpdateStepStatus } from "@/hooks";
import { useParams } from "react-router-dom";
import { AnimatedAccordion } from "../../../../components/core-components/molecules/Accordion/accordion";
import { AddedMember } from "../../components/AddedMember/added-member";
import { CreateDepartmentDrawer } from "../../components/CreateDepartmentDrawer/create-department-drawer";
import { CreateJobRoleDrawer } from "../../components/CreateJobRoleDrawer/create-job-role-drawer";
import { CreateOptions } from "../../components/CreateOptions";
import { DeleteMemberModal } from "../../components/DeleteMemberModal/delete-member-modal";
import { ManageMembersLoading } from "../../components/ManageMembersLoading/manage-members-loading";
import { MigrateAccordionHeader } from "../../components/MigrateAccordionHeader/migrate-accordion-header";
import { PermissionsDrawer } from "../../components/PermissionsDrawer/permissions-drawer";
import { PERMISSION_PROFILE_TITLE_MAP, StepKeys } from "../../constants";
import { useManageMembersForm } from "../../context/manage-members-context";
import * as SC from "./manage-members.styles";

interface ManageMembersProps {
  shouldShakeAddMemberButton?: boolean;
}

export const ManageMembers = ({
  shouldShakeAddMemberButton = false,
}: ManageMembersProps) => {
  const theme = useTheme();
  const { selectedCompany } = useSelectedCompany();
  const { setupId } = useParams();
  const { updateStepStatus } = useUpdateStepStatus(selectedCompany.id);

  const [isCreateDepartmentDrawerOpen, setIsCreateDepartmentDrawerOpen] =
    useState(false);

  const [isCreateJobRoleDrawerOpen, setIsCreateJobRoleDrawerOpen] =
    useState(false);

  const handleCreateDepartmentDrawerClose = () => {
    setIsCreateDepartmentDrawerOpen(false);
  };

  const handleCreateDepartmentClick = () => {
    setIsCreateDepartmentDrawerOpen(true);
  };

  const handleCreateJobRoleClick = () => {
    setIsCreateJobRoleDrawerOpen(true);
  };

  const handleCreateJobRoleDrawerClose = () => {
    setIsCreateJobRoleDrawerOpen(false);
  };

  const {
    addMemberLoading,
    department,
    departmentsTotalCount,
    documentNumber,
    email,
    filteredAddedMembers,
    filteredDepartments,
    filteredJobRoles,
    hiringDate,
    isFormLoading,
    isDeleteMemberModalOpen,
    isPermissionsDrawerOpen,
    jobRole,
    jobRolesTotalCount,
    members,
    name,
    permissionRoles,
    permissionProfile,
    phone,
    withoutEmail,
    errors,
    isFormDisabled,
    setIsFormDisabled,
    canMigrateEmployee,
    willMigrate,
    setWillMigrate,
    getEmailHelperText,
    onAddMemberClick,
    onDeleteMemberModalClose,
    onDeleteMemberClick,
    onDeleteMemberSubmit,
    onDepartmentBlur,
    onDepartmentChange,
    onDocumentNumberBlur,
    onDocumentNumberChange,
    onEmailBlur,
    onEmailChange,
    onHiringDateBlur,
    onHiringDateChange,
    onJobRoleBlur,
    onJobRoleChange,
    onNameBlur,
    onNameChange,
    onPermissionsDrawerClose,
    onPermissionsDrawerOpen,
    onPermissionsDrawerSubmit,
    onPhoneBlur,
    onPhoneChange,
    onSearchDepartmentTermChange,
    onSearchMembersTermChange,
    onSearchJobRoleTermChange,
    onWithoutEmailChange,
  } = useManageMembersForm();

  useEffect(() => {
    if (setupId) {
      updateStepStatus({
        setupId,
        externalId: StepKeys.ADD_MEMBERS,
        status: "DOING",
      });
    }
  }, [setupId, updateStepStatus]);

  if (isFormLoading) {
    return <ManageMembersLoading />;
  }

  const membersAddedCount = members ? members.length : 0;

  return (
    <>
      <SC.ResponsiveContainer>
        <SC.Grid>
          <SC.LeftSection>
            <MaskedScrollArea>
              <Box flexDirection="column" gap={"s"} $px="xs2">
                <Box flexDirection="column" gap={"xs5"}>
                  <Typography.Headline7 weight={700} color="neutral.20">
                    Cadastre membros e administradores da empresa
                  </Typography.Headline7>
                  <Typography.Body4 weight={400} color="neutral.40">
                    As pessoas cadastradas receberão o convite de acesso no
                    contato informado.
                  </Typography.Body4>
                </Box>
                <Box flexDirection="column" gap={"xs"}>
                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box flex={0.3} flexDirection="column">
                      <TextField
                        fullWidth
                        onBlur={onDocumentNumberBlur}
                        onChange={(event) =>
                          onDocumentNumberChange(event.target.value)
                        }
                        label={"CPF*"}
                        error={!!errors?.documentNumber}
                        helperText={errors?.documentNumber}
                        value={
                          documentNumber &&
                          documentNumberFormatter(documentNumber)
                        }
                      />
                    </Box>
                    <Box flex={0.7} flexDirection="column">
                      <TextField
                        fullWidth
                        onBlur={onNameBlur}
                        onChange={(event) => onNameChange(event.target.value)}
                        label={"Nome completo*"}
                        error={!isFormDisabled && !!errors?.name}
                        helperText={!isFormDisabled && errors?.name}
                        value={name}
                        disabled={isFormDisabled}
                      />
                    </Box>
                  </Box>

                  {canMigrateEmployee !== undefined && (
                    <AnimatedAccordion
                      defaultExpanded
                      customHeader={
                        <MigrateAccordionHeader
                          canMigrate={canMigrateEmployee}
                        />
                      }
                    >
                      <CreateOptions
                        canMigrate={canMigrateEmployee}
                        willMigrate={willMigrate}
                        setWillMigrate={setWillMigrate}
                        setIsFormDisabled={setIsFormDisabled}
                      />
                    </AnimatedAccordion>
                  )}

                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box flex={0.3} flexDirection="column">
                      <TextField
                        fullWidth
                        onBlur={onPhoneBlur}
                        onChange={(event) => onPhoneChange(event.target.value)}
                        label={"Celular"}
                        error={!isFormDisabled && !!errors?.phone}
                        helperText={!isFormDisabled && errors?.phone}
                        value={phone && phoneFormatter(phone)}
                        disabled={isFormDisabled}
                      />
                    </Box>
                    <Box flex={0.7}>
                      <Box gap={"xs4"} flex={1} alignItems="flex-start">
                        <Box flexDirection="column" flex={1}>
                          <TextField
                            disabled={isFormDisabled || withoutEmail}
                            fullWidth
                            onBlur={onEmailBlur}
                            onChange={(event) =>
                              onEmailChange(event.target.value)
                            }
                            label={withoutEmail ? "E-mail" : "E-mail*"}
                            error={!isFormDisabled && !!errors?.email}
                            helperText={getEmailHelperText()}
                            size="medium"
                            value={email}
                          />
                        </Box>
                        <Box alignItems="center">
                          <Checkbox
                            checked={withoutEmail}
                            onChange={(_, value) => onWithoutEmailChange(value)}
                            disabled={isFormDisabled}
                          />
                          <Typography.Body4 weight={600} color="neutral.30">
                            Sem e-mail
                          </Typography.Body4>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box flex={1} flexDirection="column">
                      <Dropdown.Root
                        label={"Cargo*"}
                        error={!isFormDisabled && !!errors?.jobRole}
                        mode={DropdownModes.SingleSelect}
                        size={DropdownSizes.Md}
                        helperText={!isFormDisabled ? errors?.jobRole : ""}
                        disabled={isFormDisabled}
                        selectedOption={
                          jobRole
                            ? {
                                id: jobRole.id,
                                label: jobRole.name,
                                value: jobRole,
                              }
                            : null
                        }
                        onBlur={onJobRoleBlur}
                      >
                        <Dropdown.Value>
                          <EllipsisTypography
                            variant="body4"
                            weight={600}
                            color="neutral.30"
                          >
                            {jobRole?.name}
                          </EllipsisTypography>
                        </Dropdown.Value>
                        <Box
                          $width={"100%"}
                          $minHeight={"300px"}
                          $height={"100%"}
                          flexDirection="column"
                          gap="xs4"
                        >
                          {!!jobRolesTotalCount && (
                            <SearchField
                              label={"Buscar"}
                              onChange={(e) => {
                                onSearchJobRoleTermChange(e.target.value);
                              }}
                              inputSize="md"
                              onClear={() => onSearchJobRoleTermChange("")}
                            />
                          )}
                          <Dropdown.OptionsWrapper>
                            {filteredJobRoles?.map((role) => {
                              const checked = role.id === jobRole?.id;

                              return (
                                <Dropdown.Option
                                  checked={checked}
                                  key={role.id}
                                  id={role.id}
                                  label={role.name}
                                  onCheckedChange={(_, { value }) => {
                                    onJobRoleChange(value);
                                  }}
                                  value={role}
                                >
                                  <Box
                                    alignItems="center"
                                    gap="xs3"
                                    $minWidth={0}
                                  >
                                    <SC.OptionLabel
                                      variant="body3"
                                      color={
                                        checked ? "secondary.40" : "neutral.30"
                                      }
                                      weight={600}
                                    >
                                      {role.name}
                                    </SC.OptionLabel>
                                    {checked && (
                                      <Icons
                                        name="IconCheck"
                                        color={theme.colors.secondary[50]}
                                        size={24}
                                      />
                                    )}
                                  </Box>
                                </Dropdown.Option>
                              );
                            })}
                          </Dropdown.OptionsWrapper>
                          <Divider orientation="horizontal" />
                          <Box justifyContent="center" $py="xs3">
                            <LinkButton
                              variant="default"
                              size="medium"
                              onClick={handleCreateJobRoleClick}
                            >
                              Novo cargo <Icons name="IconPlus" size={16} />
                            </LinkButton>
                          </Box>
                        </Box>
                      </Dropdown.Root>
                    </Box>
                    <Box flex={1} flexDirection="column">
                      <Dropdown.Root
                        label={"Departamento*"}
                        error={!isFormDisabled && !!errors?.department}
                        mode={DropdownModes.SingleSelect}
                        size={DropdownSizes.Md}
                        helperText={!isFormDisabled ? errors?.department : ""}
                        onBlur={onDepartmentBlur}
                        disabled={isFormDisabled}
                        selectedOption={
                          department
                            ? {
                                id: department.id,
                                label: department.name,
                                value: department,
                              }
                            : null
                        }
                      >
                        <Dropdown.Value>
                          <EllipsisTypography
                            variant="body4"
                            weight={600}
                            color="neutral.30"
                          >
                            {department?.name}
                          </EllipsisTypography>
                        </Dropdown.Value>
                        <Box
                          $width={"100%"}
                          $minHeight={"300px"}
                          $height={"100%"}
                          flexDirection="column"
                          gap="xs4"
                        >
                          {!!departmentsTotalCount && (
                            <SearchField
                              label={"Buscar"}
                              onChange={(e) => {
                                onSearchDepartmentTermChange(e.target.value);
                              }}
                              inputSize="md"
                              onClear={() => onSearchDepartmentTermChange("")}
                            />
                          )}
                          <Dropdown.OptionsWrapper>
                            {filteredDepartments.map((filteredDepartment) => {
                              const checked =
                                filteredDepartment.id === department?.id;

                              return (
                                <Dropdown.Option
                                  checked={checked}
                                  key={filteredDepartment.id}
                                  id={filteredDepartment.id}
                                  label={filteredDepartment.name}
                                  onCheckedChange={(_, { value }) => {
                                    onDepartmentChange(value);
                                  }}
                                  value={filteredDepartment}
                                >
                                  <Box
                                    alignItems="center"
                                    gap="xs3"
                                    $minWidth={0}
                                  >
                                    <SC.OptionLabel
                                      variant="body3"
                                      color={
                                        checked ? "secondary.40" : "neutral.30"
                                      }
                                      weight={600}
                                    >
                                      {filteredDepartment.name}
                                    </SC.OptionLabel>
                                    {checked && (
                                      <Icons
                                        name="IconCheck"
                                        color={theme.colors.secondary[50]}
                                        size={24}
                                      />
                                    )}
                                  </Box>
                                </Dropdown.Option>
                              );
                            })}
                          </Dropdown.OptionsWrapper>
                          <Divider orientation="horizontal" />
                          <Box justifyContent="center" $py="xs3">
                            <LinkButton
                              variant="default"
                              size="medium"
                              onClick={handleCreateDepartmentClick}
                            >
                              Novo departamento{" "}
                              <Icons name="IconPlus" size={16} />
                            </LinkButton>
                          </Box>
                        </Box>
                      </Dropdown.Root>
                    </Box>
                  </Box>
                  <Box gap={"xs"} flex={1} alignItems="stretch">
                    <Box
                      flex={`0 0 calc((100% - ${theme.spacings.xs}) / 2)`}
                      flexDirection="column"
                    >
                      <DatePicker
                        key={hiringDate?.toString()}
                        disabled={isFormDisabled}
                        fullWidth
                        onBlur={onHiringDateBlur}
                        onDateChange={(value) =>
                          onHiringDateChange(value?.toDate())
                        }
                        label={"Data de admissão*"}
                        placeholder="DD/MM/AAAA"
                        error={!isFormDisabled && !!errors?.hiringDate}
                        helperText={!isFormDisabled && errors?.hiringDate}
                        value={hiringDate && dayjs(hiringDate)}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box
                  flex={1}
                  justifyContent="space-between"
                  alignItems="center"
                  gap="xs3"
                  $border={`${theme.borders.width.xs2} solid ${theme.colors.neutral[90]}`}
                  $borderRadius={theme.borders.radius.m}
                  $px="xs1"
                  $py="xs2"
                >
                  <div>
                    <EllipsisTypography
                      variant="headline9"
                      color={"neutral.30"}
                      weight={700}
                    >
                      Nível de permissão
                    </EllipsisTypography>
                    {permissionProfile && (
                      <EllipsisTypography
                        variant="caption"
                        color={"neutral.40"}
                        weight={400}
                      >
                        {PERMISSION_PROFILE_TITLE_MAP[permissionProfile]}
                      </EllipsisTypography>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    variantType="neutral"
                    onClick={onPermissionsDrawerOpen}
                    disabled={isFormDisabled}
                  >
                    Trocar
                    <Icons name="IconRepeat" size={16} />
                  </Button>
                </Box>
                <Box flex={1}>
                  <SC.AddMembersButton
                    variant="secondary"
                    variantType="default"
                    loading={addMemberLoading}
                    onClick={onAddMemberClick}
                    shouldShake={shouldShakeAddMemberButton}
                  >
                    Salvar
                    <Icons
                      name="IconCheck"
                      color={theme.colors.secondary[50]}
                      size={16}
                    />
                  </SC.AddMembersButton>
                </Box>
              </Box>
            </MaskedScrollArea>
          </SC.LeftSection>
          <SC.RightSection>
            <Box
              flexDirection="column"
              $minHeight={"100%"}
              $border={`${theme.borders.width.xs2} solid ${theme.colors.neutral[90]}`}
              $borderRadius={theme.borders.radius.m}
              $py="xs2"
            >
              <MaskedScrollArea>
                <Box
                  flexDirection="column"
                  gap="xs2"
                  $minHeight={"100%"}
                  $px="xs2"
                >
                  <Box gap="xs5">
                    <Typography.Body4 color="neutral.20" weight={600}>
                      Membros da equipe adicionados
                    </Typography.Body4>
                    <div>
                      <Tag as="span" variant="gray" size="extra-small">
                        {membersAddedCount}
                      </Tag>
                    </div>
                  </Box>
                  <SearchField
                    label={"Buscar"}
                    onChange={(e) => {
                      onSearchMembersTermChange(e.target.value);
                    }}
                    inputSize="md"
                    onClear={() => onSearchMembersTermChange("")}
                  />
                  <Tip.Root>
                    <Tip.Icon
                      variant="neutral"
                      name="IconBulb"
                      color={theme.colors.neutral[40]}
                      size={24}
                    />
                    <Tip.Content>
                      <Typography
                        variant="caption"
                        color="neutral.30"
                        weight={600}
                      >
                        Alguns membros já foram cadastrados. Você poderá manter
                        ou editá-los.
                      </Typography>
                    </Tip.Content>
                  </Tip.Root>
                  <Box flexDirection="column" flex={1}>
                    {filteredAddedMembers?.map(
                      ({ documentNumber, id, name }) => (
                        <AddedMember
                          companyName={selectedCompany.name}
                          documentNumber={documentNumberFormatter(
                            documentNumber
                          )}
                          name={name}
                          onDeleteClick={() => onDeleteMemberClick(id)}
                          key={id}
                        />
                      )
                    )}
                  </Box>
                </Box>
              </MaskedScrollArea>
            </Box>
          </SC.RightSection>
        </SC.Grid>
      </SC.ResponsiveContainer>
      <PermissionsDrawer
        onClose={onPermissionsDrawerClose}
        open={isPermissionsDrawerOpen}
        onSubmit={onPermissionsDrawerSubmit}
        defaultPermissionProfile={permissionProfile}
        defaultPermissionRoles={permissionRoles}
      />
      <CreateDepartmentDrawer
        open={isCreateDepartmentDrawerOpen}
        onClose={handleCreateDepartmentDrawerClose}
        onSuccess={onDepartmentChange}
      />
      <CreateJobRoleDrawer
        open={isCreateJobRoleDrawerOpen}
        onClose={handleCreateJobRoleDrawerClose}
        onSuccess={onJobRoleChange}
      />
      <DeleteMemberModal
        open={isDeleteMemberModalOpen}
        onSubmit={onDeleteMemberSubmit}
        onClose={onDeleteMemberModalClose}
      />
    </>
  );
};
